import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@material-ui/core";
import { Info } from "@material-ui/icons";

import SessionStatusChip from "./sessionStatusChip";
import {
  formatDateTime,
  getEmail,
  getPhotoCount,
  getTeamName,
  hasValue,
  isSessionLive,
} from "../sessionUtils";

const TeamPhotosListView = ({
  state,
  classes,
  onSessionClick,
  onPageChange,
  onRowsPerPageChange,
  onSortColumn,
}) => {
  const { t } = useTranslation();

  // Le tri serveur est toujours décroissant : on n'affiche donc qu'un sens.
  const SortableHeader = ({ column, label }) => (
    <TableCell className={classes.tableHeaderCell}>
      <TableSortLabel
        active={state.sortColumn === column}
        direction="desc"
        onClick={() => onSortColumn(column)}
        className={classes.sortLabel}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="sessions">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}>
                {t("teamPhotos.table.status")}
              </TableCell>
              <SortableHeader
                column="created_at"
                label={t("teamPhotos.table.started_at")}
              />
              <TableCell className={classes.tableHeaderCell}>
                {t("teamPhotos.table.team_name")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell}>
                {t("teamPhotos.table.email")}
              </TableCell>
              <SortableHeader
                column="pointsWon"
                label={t("teamPhotos.carousel.points_won")}
              />
              <SortableHeader
                column="totalTime"
                label={t("teamPhotos.carousel.total_time")}
              />
              <TableCell className={classes.tableHeaderCell}>
                {t("teamPhotos.table.photos")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell}>
                {t("teamPhotos.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.sessions.map((session) => {
              const live = isSessionLive(session);

              return (
                <TableRow
                  key={session.id}
                  className={`${classes.tableRow} ${
                    live ? classes.liveRow : ""
                  }`}
                >
                  <TableCell className={classes.tableCell}>
                    <SessionStatusChip session={session} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body2">
                      {formatDateTime(session.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body2" className={classes.teamName}>
                      {getTeamName(session, t)}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body2" className={classes.email}>
                      {getEmail(session) || t("teamPhotos.no_email")}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body2">
                      {hasValue(session.pointsWon) ? session.pointsWon : "—"}
                      {session.totalPoints ? ` / ${session.totalPoints}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body2">
                      {hasValue(session.totalTime)
                        ? `${session.totalTime} ${t(
                            "teamPhotos.carousel.minutes"
                          )}`
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Chip
                      label={`${getPhotoCount(session)} ${t(
                        "teamPhotos.table.photo_count"
                      )}`}
                      size="small"
                      className={classes.photoCountChip}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <IconButton
                      onClick={() => onSessionClick(session.id)}
                      className={classes.photoButton}
                      title={t("teamPhotos.table.view_info")}
                    >
                      <Info />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {state.sessions.length === 0 && !state.loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography variant="h6" color="textSecondary">
            {t("teamPhotos.no_photos")}
          </Typography>
        </Box>
      )}

      <TablePagination
        component="div"
        count={state.totalRows}
        page={state.page}
        onPageChange={onPageChange}
        rowsPerPage={state.rowPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t("teamPhotos.pagination.rows_per_page")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("teamPhotos.pagination.of")} ${count}`
        }
        className={classes.pagination}
      />
    </Box>
  );
};

export default TeamPhotosListView;
